import { useState, useEffect, useCallback, useContext } from 'react';
import { useAuth } from 'lib/Auth';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { GroupedUserPermissions, UserPermission } from 'data/UserPermissions';
import { applyPermissionChanges, getDependentChanges, mergePermissionChanges } from 'utils/permissionDependencies';
import { getUserPermissionsByRole } from 'requests/userPermissions';
import { getPermissionAccessByCode, isPermissionReadOnly } from 'utils/authorization';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import { BusinessUnitContext } from 'context/businessUnit';
import { VendorContext } from 'context/vendor';

interface UserRoleHookProps {
    roleId: number;
    setEditedPermissions: React.Dispatch<React.SetStateAction<UserPermission[]>>;
    setHasUnsavedChanges: (value: boolean) => void;
}

interface UserPermissionsReturn {
    // Data state
    permissions: GroupedUserPermissions[];
    loading: boolean;

    // CRUD actions
    handleEdit: (permission: UserPermission, readAccess: boolean, writeAccess: boolean) => void;

    // Utility actions
    refreshData: () => void;
    resetState: () => void;
}

export const useUserRoles = ({
    roleId,
    setEditedPermissions,
    setHasUnsavedChanges
}: UserRoleHookProps): UserPermissionsReturn => {
    const { token } = useAuth();

    // Data state
    const [permissions, setPermissions] = useState<GroupedUserPermissions[]>([]);
    const [originalPermissions, setOriginalPermissions] = useState<GroupedUserPermissions[]>([]);
    const [loading, setLoading] = useState(false);

    // Helper function to create a deep copy of permissions
    const deepCopyPermissions = (perms: GroupedUserPermissions[]): GroupedUserPermissions[] => {
        return perms.map(group => ({
            ...group,
            data: group.data.map(permission => ({ ...permission }))
        }));
    };

    // Helper function to check if permissions have changed
    const hasPermissionsChanged = useCallback((current: GroupedUserPermissions[], original: GroupedUserPermissions[]): boolean => {
        return JSON.stringify(current) !== JSON.stringify(original);
    }, []);

    // Reset all state to initial values
    const resetState = useCallback(() => {
        setPermissions([]);
        setOriginalPermissions([]);
        setLoading(false);
    }, []);

    // Fetch business unit users
    const fetchUserPermissions = useCallback(async (showLoading = true) => {
        if (!roleId || !token) {
            setPermissions([]);
            setOriginalPermissions([]);
            return;
        }

        try {
            if (showLoading) {
                setLoading(true);
            }

            console.log('Fetching user permissions with params:', {
                roleId,
            });

            const response = await getUserPermissionsByRole(
                [
                    roleId,
                ],
                { token }
            );

            if (response && Array.isArray(response)) {
                console.log('Successfully fetched user permissions:', {
                    roleId,
                    dataLength: response.length
                });

                const permissionsData = response as GroupedUserPermissions[];
                setPermissions(permissionsData);

                // Store original permissions for comparison
                setOriginalPermissions(deepCopyPermissions(permissionsData));

                // Reset edited permissions and unsaved changes when new data loads
                setEditedPermissions([]);
                setHasUnsavedChanges(false);
            } else {
                console.warn('Unexpected response structure:', response);
            }

        } catch (err: any) {
            console.error('Error fetching business unit users:', err);
            renderToast(toast.TYPE.ERROR, err.message);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [roleId, token, setEditedPermissions, setHasUnsavedChanges]);

    // Initial load and dependency-based reload
    useEffect(() => {
        fetchUserPermissions();
    }, [fetchUserPermissions]);

    // CRUD action handlers
    const handleEdit = useCallback(
        (permission: UserPermission, readAccess: boolean, writeAccess: boolean) => {
            const changes: UserPermission[] = [
                { ...permission, readAccess, writeAccess },
                ...getDependentChanges(permission, writeAccess, permissions),
            ];

            // Update edited permissions array
            setEditedPermissions((prev: UserPermission[]) => mergePermissionChanges(prev, changes));

            // Update current permissions state
            const updatedPermissions = applyPermissionChanges(permissions, changes);

            setPermissions(updatedPermissions);

            // Check if there are unsaved changes by comparing with original
            const hasChanges = hasPermissionsChanged(updatedPermissions, originalPermissions);
            setHasUnsavedChanges(hasChanges);
        },
        [permissions, originalPermissions, hasPermissionsChanged, setEditedPermissions, setHasUnsavedChanges]
    );

    // Utility actions
    const refreshData = useCallback(() => {
        fetchUserPermissions(true);
    }, [fetchUserPermissions]);

    return {
        // Data state
        permissions,
        loading,

        // CRUD actions
        handleEdit,

        // Utility actions
        refreshData,
        resetState
    };
};

const checkUserPermissionFeatureFlag = () => {
    const featureFlags = getItems(STORAGE_KEYS.FEATURE_FLAGS);
    const isUserPermissionEnabled = featureFlags ? featureFlags?.businessUnit?.enabled : null;
    return { isUserPermissionEnabled };
};

// Common hook to check permissions
const usePermissionCheck = (permissionCode: string) => {
    const { isUserPermissionEnabled } = checkUserPermissionFeatureFlag();
    const permission = isUserPermissionEnabled ? getPermissionAccessByCode(permissionCode) : null;
    
    return { isUserPermissionEnabled, permission };
};

export const useReadPermission = (permissionCode: string) => {
    const { isUserPermissionEnabled, permission } = usePermissionCheck(permissionCode);
    const { user } = useAuth();
    // Try to get business unit context, but don't throw error if not available
    // This allows the hook to work in components outside BusinessUnitProvider (e.g., auth components)
    const businessUnitContext = useContext(BusinessUnitContext);
    const selectedBusinessUnit = businessUnitContext?.selectedBusinessUnit;
    const vendorContext = useContext(VendorContext);
    const selectedVendor = vendorContext?.selectedVendor;

    const [readOnly, setReadOnly] = useState(false);

    useEffect(() => {
        setReadOnly(isPermissionReadOnly(permissionCode, user, selectedBusinessUnit, selectedVendor?.id));
    }, [permissionCode, isUserPermissionEnabled, permission, selectedBusinessUnit, selectedVendor, user]);

    return { readOnly };
};

// Specific to reports page where we want to hide certain reports based on show/hide access
export const useShowReportsPermission = (permissionCode: string) => {
    const { isUserPermissionEnabled, permission } = usePermissionCheck(permissionCode);
    const [show, setShow] = useState(!isUserPermissionEnabled);

    useEffect(() => {
        if (isUserPermissionEnabled && permission?.readAccess) {
            setShow(true);
        }
    }, [permissionCode, isUserPermissionEnabled, permission]);

    return { show };
};